import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { 
  FileText, Plus, Trash2, Calendar, User, Download, Loader2, 
  Stethoscope, TestTube, Pill, ChevronRight, Search, Filter
} from 'lucide-react';
import { medicalAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import FileUploader from './FileUploader';

const MedicalRecordsTab = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    record_type: 'consultation',
    doctor: '',
    diagnosis: '',
    description: '',
    medication: '',
    dosage: '',
    duration: '',
    analysis_type: '',
    results: '',
  });

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      const data = await medicalAPI.getRecords();
      setRecords(data);
    } catch (error) {
      console.error('Error loading records:', error);
      toast({ title: 'Ошибка', description: 'Не удалось загрузить записи', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRecord = async () => {
    try {
      const result = await medicalAPI.createRecord(formData);
      if (uploadedFiles.length > 0) {
        await medicalAPI.attachFiles(result.record.id, uploadedFiles);
      }
      toast({ title: 'Запись создана', description: 'Медицинская запись добавлена' });
      setIsDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        record_type: 'consultation',
        doctor: '', diagnosis: '', description: '',
        medication: '', dosage: '', duration: '',
        analysis_type: '', results: '',
      });
      setUploadedFiles([]);
      loadRecords();
    } catch (error) {
      console.error('Error creating record:', error);
      toast({ title: 'Ошибка', description: 'Не удалось создать запись', variant: 'destructive' });
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Удалить эту запись?')) return;
    try {
      await medicalAPI.deleteRecord(recordId);
      toast({ title: 'Запись удалена' });
      loadRecords();
    } catch (error) {
      console.error('Error deleting record:', error);
      toast({ title: 'Ошибка', description: 'Не удалось удалить запись', variant: 'destructive' });
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const blob = await medicalAPI.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({ title: 'Ошибка', description: 'Не удалось скачать файл', variant: 'destructive' });
    }
  };

  const getRecordConfig = (type) => {
    const configs = {
      consultation: {
        label: 'Консультация',
        icon: Stethoscope,
        color: 'from-blue-500/40 to-cyan-500/40',
        borderColor: 'border-blue-400/40',
        iconColor: 'text-blue-400',
        badge: 'bg-blue-500/30 text-blue-300'
      },
      analysis: {
        label: 'Анализ',
        icon: TestTube,
        color: 'from-green-500/40 to-emerald-500/40',
        borderColor: 'border-green-400/40',
        iconColor: 'text-green-400',
        badge: 'bg-green-500/30 text-green-300'
      },
      prescription: {
        label: 'Рецепт',
        icon: Pill,
        color: 'from-purple-500/40 to-pink-500/40',
        borderColor: 'border-purple-400/40',
        iconColor: 'text-purple-400',
        badge: 'bg-purple-500/30 text-purple-300'
      },
    };
    return configs[type] || configs.consultation;
  };

  const filteredRecords = records
    .filter(record => filter === 'all' || record.record_type === filter)
    .filter(record => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        record.doctor?.toLowerCase().includes(query) ||
        record.diagnosis?.toLowerCase().includes(query) ||
        record.description?.toLowerCase().includes(query) ||
        record.medication?.toLowerCase().includes(query)
      );
    });

  // Stats
  const consultationsCount = records.filter(r => r.record_type === 'consultation').length;
  const analysesCount = records.filter(r => r.record_type === 'analysis').length;
  const prescriptionsCount = records.filter(r => r.record_type === 'prescription').length;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards - Liquid Glass */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <button
          onClick={() => setFilter(filter === 'consultation' ? 'all' : 'consultation')}
          className={`liquid-glass-card p-4 text-left ${filter === 'consultation' ? 'ring-2 ring-blue-400/30' : ''}`}
        >
          <div className="relative z-10">
            <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-2">
              <Stethoscope className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{consultationsCount}</div>
            <div className="text-white/60 text-[10px] sm:text-xs">Консультации</div>
          </div>
        </button>

        <button
          onClick={() => setFilter(filter === 'analysis' ? 'all' : 'analysis')}
          className={`liquid-glass-card p-4 text-left ${filter === 'analysis' ? 'ring-2 ring-green-400/30' : ''}`}
        >
          <div className="relative z-10">
            <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-2">
              <TestTube className="w-5 h-5 sm:w-6 sm:h-6 text-green-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{analysesCount}</div>
            <div className="text-white/60 text-[10px] sm:text-xs">Анализы</div>
          </div>
        </button>

        <button
          onClick={() => setFilter(filter === 'prescription' ? 'all' : 'prescription')}
          className={`liquid-glass-card p-4 text-left ${filter === 'prescription' ? 'ring-2 ring-purple-400/30' : ''}`}
        >
          <div className="relative z-10">
            <div className="liquid-glass-icon w-10 h-10 flex items-center justify-center mb-2">
              <Pill className="w-5 h-5 sm:w-6 sm:h-6 text-purple-400" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-white">{prescriptionsCount}</div>
            <div className="text-white/60 text-[10px] sm:text-xs">Рецепты</div>
          </div>
        </button>
      </div>

      {/* Search & Actions - Liquid Glass */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по записям..."
            className="pl-10 bg-white/[0.06] backdrop-blur-xl border-white/[0.12] text-white placeholder:text-white/40 rounded-full h-12"
          />
        </div>
        
        {filter !== 'all' && (
          <Button
            onClick={() => setFilter('all')}
            className="bg-white/[0.08] hover:bg-white/[0.15] text-white border border-white/[0.15] rounded-full h-12 backdrop-blur-sm"
          >
            <Filter className="mr-2" size={16} />
            Сбросить
          </Button>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-white/[0.08] hover:bg-white/[0.15] text-white border border-white/[0.15] rounded-full h-12 px-6 transition-all backdrop-blur-sm">
              <Plus className="mr-2" size={18} />
              Добавить
            </Button>
          </DialogTrigger>
          <DialogContent className="liquid-glass-card border-white/[0.15] text-white max-w-2xl max-h-[90vh] overflow-y-auto" style={{ background: 'rgba(28, 28, 30, 0.85)', backdropFilter: 'blur(40px) saturate(180%)' }}>
            <DialogHeader>
              <DialogTitle className="text-white text-xl relative z-10">Новая запись</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4 relative z-10">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-white/70 text-sm">Дата</label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="bg-white/5 border-white/20 text-white rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-white/70 text-sm">Тип</label>
                  <select
                    value={formData.record_type}
                    onChange={(e) => setFormData({ ...formData, record_type: e.target.value })}
                    className="w-full bg-white/5 border border-white/20 text-white rounded-xl px-3 py-2"
                  >
                    <option value="consultation" className="bg-gray-900">Консультация</option>
                    <option value="analysis" className="bg-gray-900">Анализ</option>
                    <option value="prescription" className="bg-gray-900">Рецепт</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-white/70 text-sm">Врач</label>
                <Input
                  value={formData.doctor}
                  onChange={(e) => setFormData({ ...formData, doctor: e.target.value })}
                  placeholder="Имя врача"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                />
              </div>

              {(formData.record_type === 'consultation' || formData.record_type === 'prescription') && (
                <div className="space-y-2">
                  <label className="text-white/70 text-sm">Диагноз</label>
                  <Input
                    value={formData.diagnosis}
                    onChange={(e) => setFormData({ ...formData, diagnosis: e.target.value })}
                    placeholder="Диагноз"
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="text-white/70 text-sm">Описание</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Дополнительная информация"
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl resize-none"
                  rows={3}
                />
              </div>

              {formData.record_type === 'prescription' && (
                <div className="bg-white/5 rounded-2xl p-4 space-y-3">
                  <div className="space-y-2">
                    <label className="text-white/70 text-sm">Лекарство</label>
                    <Input
                      value={formData.medication}
                      onChange={(e) => setFormData({ ...formData, medication: e.target.value })}
                      placeholder="Название"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="text-white/70 text-sm">Дозировка</label>
                      <Input
                        value={formData.dosage}
                        onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                        placeholder="2 раза в день"
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-white/70 text-sm">Длительность</label>
                      <Input
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        placeholder="7 дней"
                        className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                      />
                    </div>
                  </div>
                </div>
              )}

              {formData.record_type === 'analysis' && (
                <div className="bg-white/5 rounded-2xl p-4 space-y-3">
                  <div className="space-y-2">
                    <label className="text-white/70 text-sm">Тип анализа</label>
                    <Input
                      value={formData.analysis_type}
                      onChange={(e) => setFormData({ ...formData, analysis_type: e.target.value })}
                      placeholder="Общий анализ крови"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-white/70 text-sm">Результаты</label>
                    <Textarea
                      value={formData.results}
                      onChange={(e) => setFormData({ ...formData, results: e.target.value })}
                      placeholder="Результаты анализа"
                      className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-xl resize-none"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-white/70 text-sm">Файлы (до 20)</label>
                <FileUploader onFilesUploaded={setUploadedFiles} maxFiles={20} />
              </div>

              <Button
                onClick={handleCreateRecord}
                disabled={!formData.doctor}
                className="w-full bg-white/[0.1] hover:bg-white/[0.15] text-white border border-white/[0.15] rounded-2xl h-12 font-medium disabled:opacity-50 transition-all"
              >
                Создать запись
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Records List - Liquid Glass */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-white/60" size={48} />
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="liquid-glass-card p-12 text-center">
          <FileText className="mx-auto mb-4 text-white/30 relative z-10" size={64} />
          <p className="text-white/70 text-lg relative z-10">Нет медицинских записей</p>
          <p className="text-white/50 text-sm mt-2 relative z-10">Добавьте первую запись для начала</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRecords.map((record) => {
            const config = getRecordConfig(record.record_type);
            const Icon = config.icon;
            
            return (
              <div 
                key={record.id} 
                className="liquid-glass-card p-4 sm:p-5"
              >
                <div className="relative z-10 flex items-start gap-4">
                  <div className="liquid-glass-icon p-3 flex-shrink-0">
                    <Icon className={`w-6 h-6 ${config.iconColor}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.badge} backdrop-blur-sm`}>
                        {config.label}
                      </span>
                      <span className="text-white/60 text-xs flex items-center gap-1">
                        <Calendar size={12} />
                        {record.date}
                      </span>
                      <span className="text-white/60 text-xs flex items-center gap-1">
                        <User size={12} />
                        {record.doctor}
                      </span>
                    </div>

                    {record.diagnosis && (
                      <p className="text-white font-medium text-sm mb-1">
                        {record.diagnosis}
                      </p>
                    )}

                    {record.description && (
                      <p className="text-white/70 text-xs line-clamp-2 mb-2">{record.description}</p>
                    )}

                    {record.medication && (
                      <div className="bg-black/20 backdrop-blur-sm rounded-xl p-3 mb-2 border border-white/[0.06]">
                        <p className="text-white text-sm font-medium">{record.medication}</p>
                        <div className="flex gap-3 mt-1 text-white/60 text-xs">
                          {record.dosage && <span>Доза: {record.dosage}</span>}
                          {record.duration && <span>Курс: {record.duration}</span>}
                        </div>
                      </div>
                    )}

                    {record.analysis_type && (
                      <div className="bg-black/20 backdrop-blur-sm rounded-xl p-3 mb-2 border border-white/[0.06]">
                        <p className="text-white text-sm font-medium">{record.analysis_type}</p>
                        {record.results && (
                          <p className="text-white/60 text-xs mt-1 line-clamp-2">{record.results}</p>
                        )}
                      </div>
                    )}

                    {record.files && record.files.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {record.files.map((file) => (
                          <button
                            key={file.id}
                            onClick={() => handleDownloadFile(file.id, file.filename)}
                            className="flex items-center gap-1 bg-white/[0.06] hover:bg-white/[0.12] backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-white/80 transition-colors border border-white/[0.08]"
                          >
                            <Download size={12} />
                            {file.filename.length > 15 ? file.filename.slice(0, 15) + '...' : file.filename}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => handleDeleteRecord(record.id)}
                    className="p-2 hover:bg-red-500/20 rounded-full transition-colors flex-shrink-0"
                  >
                    <Trash2 size={18} className="text-red-400/70 hover:text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer Note */}
      <p className="text-center text-white/40 text-xs">
        * Все записи защищены шифрованием и доступны только вам
      </p>
    </div>
  );
};

export default MedicalRecordsTab;
